/**
 * Padrón de Miembros Oficiales AMPI Riviera Nayarit
 *
 * Permite subir un CSV con la lista oficial de miembros (nombre, correo, telefono),
 * matchear contra asesor/asociado existentes en Firestore, y marcar como
 * miembro_oficial: true. Luego propaga el flag a propiedades.
 */

import React, { useState, useCallback } from 'react';
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  query,
  where,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import { normalize, calculateSimilarity } from '../../utils/matching';
import { propagarMiembroOficial } from '../../utils/propagarMiembroOficial';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

// ============ INTERFACES ============

interface CsvRow {
  nombre: string;
  correo: string;
  telefono: string;
  tipo: 'ASOCIADO' | 'AFILIADO' | '';
}

interface FirestoreMember {
  docId: string;
  collection: 'asesor' | 'asociado';
  nombre: string;
  correo: string;
  telefono?: string;
  nombre_flex?: string;
  correo_flex?: string;
  miembro_oficial?: boolean;
}

type MatchStatus = 'auto' | 'review' | 'no_match';

interface MatchEntry {
  csv: CsvRow;
  status: MatchStatus;
  member?: FirestoreMember;
  confidence: number;
  method: string;
  confirmed: boolean; // Admin confirmó/rechazó manualmente
  rejected: boolean;
  createNew: boolean; // Crear perfil nuevo para sin-match
}

interface MemberToRemove {
  docId: string;
  collection: 'asesor' | 'asociado';
  nombre: string;
  correo: string;
  keep: boolean; // Admin decide mantenerlo como oficial
}

type Step = 'upload' | 'preview' | 'applying' | 'done';

// ============ COMPONENT ============

export default function PadronMiembrosOficiales() {
  const { user } = useUser();
  const [step, setStep] = useState<Step>('upload');
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
  const [members, setMembers] = useState<FirestoreMember[]>([]);
  const [matches, setMatches] = useState<MatchEntry[]>([]);
  const [toRemove, setToRemove] = useState<MemberToRemove[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<string[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | MatchStatus>('all');

  // ============ CSV PARSING ============

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split(/\r?\n/).filter(l => l.trim());

      if (lines.length < 2) {
        alert('El CSV debe tener al menos un encabezado y una fila de datos');
        return;
      }

      // Detectar separador
      const header = lines[0];
      const sep = header.includes('\t') ? '\t' : header.includes(';') ? ';' : ',';
      const headers = header.split(sep).map(h => h.trim().toLowerCase().replace(/['"]/g, ''));

      // Buscar índices de columnas (flexible)
      const iNombre = headers.findIndex(h => h.includes('nombre'));
      const iCorreo = headers.findIndex(h => h.includes('correo') || h.includes('email') || h.includes('mail'));
      const iTelefono = headers.findIndex(h => h.includes('telefono') || h.includes('tel') || h.includes('phone') || h.includes('numero'));
      const iTipo = headers.findIndex(h => h.includes('tipo'));

      if (iNombre === -1 && iCorreo === -1) {
        alert('No se encontraron columnas de nombre o correo en el CSV. Se esperan: nombre, correo, numero/telefono, tipo');
        return;
      }

      const rows: CsvRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(sep).map(v => v.trim().replace(/^["']|["']$/g, ''));
        const nombre = iNombre >= 0 ? values[iNombre] || '' : '';
        const correo = iCorreo >= 0 ? values[iCorreo] || '' : '';
        const telefono = iTelefono >= 0 ? values[iTelefono] || '' : '';
        const tipoRaw = iTipo >= 0 ? (values[iTipo] || '').toUpperCase().trim() : '';
        const tipo = (tipoRaw === 'ASOCIADO' || tipoRaw === 'AFILIADO') ? tipoRaw : '' as const;

        // Filtrar filas vacías y filas de resumen (TOTAL, solo números)
        if ((nombre || correo) && tipo) {
          rows.push({ nombre, correo, telefono, tipo });
        }
      }

      setCsvRows(rows);
      addProgress(`CSV cargado: ${rows.length} miembros encontrados`);
    };
    reader.readAsText(file, 'UTF-8');
  }, []);

  const addProgress = (msg: string) => {
    setProgress(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  // ============ MATCHING ============

  const ejecutarMatching = useCallback(async () => {
    if (csvRows.length === 0) return;
    setLoading(true);
    addProgress('Cargando miembros de Firestore...');

    try {
      // Cargar todos los asesores y asociados
      const allMembers: FirestoreMember[] = [];

      const asesorSnap = await getDocs(collection(db, 'asesor'));
      asesorSnap.forEach(d => {
        const data = d.data();
        allMembers.push({
          docId: d.id,
          collection: 'asesor',
          nombre: data.nombre || '',
          correo: data.correo || '',
          telefono: data.telefono,
          nombre_flex: data.nombre_flex,
          correo_flex: data.correo_flex,
          miembro_oficial: data.miembro_oficial,
        });
      });

      const asociadoSnap = await getDocs(collection(db, 'asociado'));
      asociadoSnap.forEach(d => {
        const data = d.data();
        allMembers.push({
          docId: d.id,
          collection: 'asociado',
          nombre: data.nombre || '',
          correo: data.correo || '',
          telefono: data.telefono,
          nombre_flex: data.nombre_flex,
          correo_flex: data.correo_flex,
          miembro_oficial: data.miembro_oficial,
        });
      });

      setMembers(allMembers);
      addProgress(`${allMembers.length} miembros cargados (${asesorSnap.size} asesores, ${asociadoSnap.size} asociados)`);

      // Matchear cada fila del CSV
      const matchResults: MatchEntry[] = [];
      const matchedDocIds = new Set<string>();

      for (const csvRow of csvRows) {
        const csvCorreo = normalize(csvRow.correo);
        const csvNombre = normalize(csvRow.nombre);
        // AFILIADO → asesor collection, ASOCIADO → asociado collection
        const expectedCollection = csvRow.tipo === 'AFILIADO' ? 'asesor' : csvRow.tipo === 'ASOCIADO' ? 'asociado' : '';

        let bestMatch: FirestoreMember | undefined;
        let bestConfidence = 0;
        let bestMethod = 'none';

        // 1. Email exacto (más confiable)
        if (csvCorreo) {
          for (const m of allMembers) {
            if (matchedDocIds.has(m.docId)) continue;
            const mCorreo = normalize(m.correo);
            const mCorreoFlex = normalize(m.correo_flex);

            if (csvCorreo === mCorreo || csvCorreo === mCorreoFlex) {
              bestMatch = m;
              bestConfidence = 100;
              bestMethod = 'email';
              break;
            }
          }
        }

        // 2. Nombre fuzzy (fallback) — priorizar colección correcta
        if (!bestMatch && csvNombre) {
          for (const m of allMembers) {
            if (matchedDocIds.has(m.docId)) continue;
            const sim1 = calculateSimilarity(csvRow.nombre, m.nombre);
            const sim2 = m.nombre_flex ? calculateSimilarity(csvRow.nombre, m.nombre_flex) : 0;
            let sim = Math.max(sim1, sim2);

            // Bonus si la colección coincide con el tipo del CSV
            if (expectedCollection && m.collection === expectedCollection) {
              sim = Math.min(100, sim + 5);
            }

            if (sim > bestConfidence) {
              bestConfidence = sim;
              bestMatch = m;
              bestMethod = 'name';
            }
          }
        }

        let status: MatchStatus;
        if (bestConfidence >= 80) {
          status = 'auto';
        } else if (bestConfidence >= 60) {
          status = 'review';
        } else {
          status = 'no_match';
          bestMatch = bestConfidence >= 40 ? bestMatch : undefined;
        }

        matchResults.push({
          csv: csvRow,
          status,
          member: bestMatch,
          confidence: bestConfidence,
          method: bestMethod,
          confirmed: status === 'auto',
          rejected: false,
          createNew: false,
        });

        if (bestMatch && status === 'auto') {
          matchedDocIds.add(bestMatch.docId);
        }
      }

      setMatches(matchResults);

      // Detectar miembros a desmarcar (oficiales actuales no en CSV)
      const csvMatchedIds = new Set(
        matchResults
          .filter(m => m.status === 'auto' || m.confirmed)
          .map(m => m.member?.docId)
          .filter(Boolean)
      );

      const currentOficiales = allMembers.filter(m => m.miembro_oficial === true);
      const removals: MemberToRemove[] = currentOficiales
        .filter(m => !csvMatchedIds.has(m.docId))
        .map(m => ({
          docId: m.docId,
          collection: m.collection,
          nombre: m.nombre,
          correo: m.correo,
          keep: false,
        }));

      setToRemove(removals);

      const autoCount = matchResults.filter(m => m.status === 'auto').length;
      const reviewCount = matchResults.filter(m => m.status === 'review').length;
      const noMatchCount = matchResults.filter(m => m.status === 'no_match').length;

      addProgress(`Matching completo: ${autoCount} auto, ${reviewCount} revisar, ${noMatchCount} sin match`);
      if (removals.length > 0) {
        addProgress(`${removals.length} miembros actuales serán desmarcados (no están en el CSV)`);
      }

      setStep('preview');
    } catch (err: any) {
      console.error('Error en matching:', err);
      addProgress(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [csvRows]);

  // ============ APLICAR PADRÓN ============

  const aplicarPadron = useCallback(async () => {
    setStep('applying');
    setLoading(true);

    try {
      // Recopilar IDs a marcar como oficial
      const idsToMark = matches
        .filter(m => m.confirmed && !m.rejected && m.member)
        .map(m => ({ docId: m.member!.docId, col: m.member!.collection }));

      // IDs a desmarcar
      const idsToUnmark = toRemove
        .filter(m => !m.keep)
        .map(m => ({ docId: m.docId, col: m.collection }));

      // Crear miembros nuevos (sin match o rechazados marcados para crear)
      const toCreate = matches.filter(m => m.createNew && (m.status === 'no_match' || m.rejected));
      let createdCount = 0;

      if (toCreate.length > 0) {
        addProgress(`Creando ${toCreate.length} miembros nuevos...`);
        for (const entry of toCreate) {
          try {
            // AFILIADO → asesor, ASOCIADO → asociado
            const col = entry.csv.tipo === 'AFILIADO' ? 'asesor' : 'asociado';
            const prefix = col === 'asesor' ? 'ase' : 'aso';
            const initials = entry.csv.nombre.split(' ').map(w => w[0]?.toLowerCase() || '').join('').slice(0, 3);
            const randomSuffix = Math.floor(Math.random() * 9000 + 1000);

            const newDoc: any = {
              nombre: entry.csv.nombre,
              correo: entry.csv.correo || '',
              telefono: entry.csv.telefono || '',
              tipo: col === 'asesor' ? 'asesor' : 'asociado',
              activo: true,
              miembro_oficial: true,
              padron_fecha: serverTimestamp(),
              fecha_registro: serverTimestamp(),
              fecha_actualizacion: serverTimestamp(),
              [`${col === 'asesor' ? 'asesor' : 'asociado'}_id`]: `${prefix}_${initials}_${randomSuffix}`,
            };

            if (col === 'asesor') {
              newDoc.inmobiliaria_doc_ids = [];
              newDoc.propiedad_id = [];
              newDoc.calificacion = 0;
            } else {
              newDoc.inmobiliaria_doc_id = '';
              newDoc.calificacion = 0;
            }

            const docRef = await addDoc(collection(db, col), newDoc);
            idsToMark.push({ docId: docRef.id, col });
            createdCount++;
          } catch (err: any) {
            addProgress(`Error creando ${entry.csv.nombre}: ${err.message}`);
          }
        }
        addProgress(`${createdCount} miembros nuevos creados`);
      }

      addProgress(`Marcando ${idsToMark.length} miembros como oficiales...`);
      addProgress(`Desmarcando ${idsToUnmark.length} miembros...`);

      // Batch writes para marcar oficiales
      let batch = writeBatch(db);
      let opsInBatch = 0;

      for (const { docId, col } of idsToMark) {
        batch.update(doc(db, col, docId), {
          miembro_oficial: true,
          padron_fecha: serverTimestamp(),
        });
        opsInBatch++;
        if (opsInBatch >= 499) {
          await batch.commit();
          batch = writeBatch(db);
          opsInBatch = 0;
        }
      }

      // Desmarcar no-oficiales
      for (const { docId, col } of idsToUnmark) {
        batch.update(doc(db, col, docId), {
          miembro_oficial: false,
          padron_fecha: serverTimestamp(),
        });
        opsInBatch++;
        if (opsInBatch >= 499) {
          await batch.commit();
          batch = writeBatch(db);
          opsInBatch = 0;
        }
      }

      if (opsInBatch > 0) {
        await batch.commit();
      }

      addProgress('Miembros actualizados. Propagando a propiedades...');

      // Propagar a propiedades
      const result = await propagarMiembroOficial((msg) => addProgress(msg));

      // Log de auditoría
      await addDoc(collection(db, 'padron_sync_log'), {
        fecha: serverTimestamp(),
        usuario: user?.nombre || user?.correo || 'admin',
        csvRows: csvRows.length,
        marcados: idsToMark.length,
        desmarcados: idsToUnmark.length,
        creados: createdCount,
        propiedadesOficiales: result.marcadasOficial,
        propiedadesNoOficiales: result.marcadasNoOficial,
        inmobiliariasOficiales: result.inmobiliariasOficiales,
      });

      setStats({
        marcados: idsToMark.length,
        desmarcados: idsToUnmark.length,
        creados: createdCount,
        ...result,
      });

      addProgress('Padrón aplicado exitosamente.');
      setStep('done');
    } catch (err: any) {
      console.error('Error aplicando padrón:', err);
      addProgress(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [matches, toRemove, csvRows, user]);

  // ============ HANDLERS ============

  const toggleConfirm = (index: number) => {
    setMatches(prev => prev.map((m, i) =>
      i === index ? { ...m, confirmed: !m.confirmed, rejected: false } : m
    ));
  };

  const toggleReject = (index: number) => {
    setMatches(prev => prev.map((m, i) => {
      if (i !== index) return m;
      const nowRejected = !m.rejected;
      return {
        ...m,
        rejected: nowRejected,
        confirmed: false,
        // Al deshacer rechazo, quitar createNew
        createNew: nowRejected ? m.createNew : false,
      };
    }));
  };

  const toggleCreateNew = (index: number) => {
    setMatches(prev => prev.map((m, i) =>
      i === index ? { ...m, createNew: !m.createNew } : m
    ));
  };

  const exportarMatching = () => {
    const headers = ['#', 'CSV_Nombre', 'CSV_Correo', 'CSV_Tipo', 'Estado', 'Match_Nombre', 'Match_Correo', 'Match_Coleccion', 'Confianza', 'Metodo', 'Confirmado', 'Rechazado', 'CrearNuevo'];
    const rows = matches.map((m, i) => [
      i + 1,
      `"${m.csv.nombre}"`,
      m.csv.correo || '',
      m.csv.tipo,
      m.status,
      `"${m.member?.nombre || ''}"`,
      m.member?.correo || '',
      m.member?.collection || '',
      m.confidence,
      m.method,
      m.confirmed ? 'SI' : 'NO',
      m.rejected ? 'SI' : 'NO',
      m.createNew ? 'SI' : 'NO',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `padron-matching-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const selectAllCreate = () => {
    setMatches(prev => prev.map(m =>
      m.status === 'no_match' ? { ...m, createNew: true } : m
    ));
  };

  const toggleKeep = (index: number) => {
    setToRemove(prev => prev.map((m, i) =>
      i === index ? { ...m, keep: !m.keep } : m
    ));
  };

  // ============ COUNTS ============

  const autoCount = matches.filter(m => m.status === 'auto').length;
  const reviewCount = matches.filter(m => m.status === 'review').length;
  const noMatchCount = matches.filter(m => m.status === 'no_match').length;
  const confirmedCount = matches.filter(m => m.confirmed && !m.rejected).length;
  const createNewCount = matches.filter(m => m.createNew).length;

  const filteredMatches = filterStatus === 'all'
    ? matches
    : matches.filter(m => m.status === filterStatus);

  // ============ RENDER ============

  const isAdmin = user?.rol === 'ADMIN' || user?.tipo?.includes?.('admin');
  if (!isAdmin) {
    return <div className="container py-5"><h3>Acceso no autorizado</h3></div>;
  }

  return (
    <>
      <UserNav />
      <Breadcrumb items={[
        { label: 'Admin', path: '/admin-dashboard' },
        { label: 'Padrón Oficial de Miembros' }
      ]} />

      <section className="py-4">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-11">

              <h4 className="mb-3">
                <i className="fa-solid fa-clipboard-check me-2 text-success"></i>
                Padrón Oficial AMPI Riviera Nayarit
              </h4>
              <p className="text-muted mb-4">
                Sube la lista oficial de miembros en CSV (nombre, correo, telefono).
                Solo las propiedades de miembros oficiales se mostrarán en el sitio público.
              </p>

              {/* ============ STEP: UPLOAD ============ */}
              {step === 'upload' && (
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <h5>1. Subir CSV</h5>
                    <p className="text-muted small">
                      Formato esperado: columnas <code>NOMBRE</code>, <code>CORREO</code>, <code>NUMERO</code>, <code>TIPO</code>
                      (separador: coma, punto y coma, o tab). TIPO: ASOCIADO o AFILIADO.
                    </p>

                    <input
                      type="file"
                      accept=".csv,.txt"
                      className="form-control mb-3"
                      onChange={handleFileUpload}
                    />

                    {csvRows.length > 0 && (
                      <>
                        <div className="alert alert-info">
                          <strong>{csvRows.length}</strong> miembros encontrados en el CSV
                        </div>

                        <div className="table-responsive mb-3" style={{ maxHeight: '300px' }}>
                          <table aria-label="Tabla de Padron Miembros Oficiales" className="table table-sm table-striped">
                            <thead className="table-dark sticky-top">
                              <tr>
                                <th scope="col">#</th>
                                <th scope="col">Nombre</th>
                                <th scope="col">Correo</th>
                                <th scope="col">Teléfono</th>
                                <th scope="col">Tipo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {csvRows.map((row, i) => (
                                <tr key={i}>
                                  <td>{i + 1}</td>
                                  <td>{row.nombre}</td>
                                  <td>{row.correo}</td>
                                  <td>{row.telefono}</td>
                                  <td>
                                    <span className={`badge ${row.tipo === 'AFILIADO' ? 'bg-info' : 'bg-secondary'}`}>
                                      {row.tipo}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <button
                          className="btn btn-primary"
                          onClick={ejecutarMatching}
                          disabled={loading}
                        >
                          {loading ? (
                            <><span className="spinner-border spinner-border-sm me-2"></span>Procesando...</>
                          ) : (
                            <><i className="fa-solid fa-search me-2"></i>Ejecutar Matching</>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* ============ STEP: PREVIEW ============ */}
              {step === 'preview' && (
                <>
                  {/* Summary cards */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <div className="card border-success bg-success bg-opacity-10">
                        <div className="card-body text-center">
                          <h3 className="text-success mb-0">{autoCount}</h3>
                          <small>Auto-matched</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card border-warning bg-warning bg-opacity-10">
                        <div className="card-body text-center">
                          <h3 className="text-warning mb-0">{reviewCount}</h3>
                          <small>Necesitan revisión</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card border-danger bg-danger bg-opacity-10">
                        <div className="card-body text-center">
                          <h3 className="text-danger mb-0">{noMatchCount}</h3>
                          <small>Sin match</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card border-primary bg-primary bg-opacity-10">
                        <div className="card-body text-center">
                          <h3 className="text-primary mb-0">{confirmedCount}</h3>
                          <small>Confirmados</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filter tabs */}
                  <ul className="nav nav-pills mb-3">
                    <li className="nav-item">
                      <button className={`nav-link ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>
                        Todos ({matches.length})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button className={`nav-link ${filterStatus === 'auto' ? 'active' : ''}`} onClick={() => setFilterStatus('auto')}>
                        Auto ({autoCount})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button className={`nav-link ${filterStatus === 'review' ? 'active' : ''}`} onClick={() => setFilterStatus('review')}>
                        Revisar ({reviewCount})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button className={`nav-link ${filterStatus === 'no_match' ? 'active' : ''}`} onClick={() => setFilterStatus('no_match')}>
                        Sin match ({noMatchCount})
                      </button>
                    </li>
                  </ul>

                  {/* Bulk actions for no_match */}
                  {(filterStatus === 'no_match' || filterStatus === 'all') && noMatchCount > 0 && (
                    <div className="d-flex gap-2 mb-2 align-items-center">
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={selectAllCreate}
                      >
                        <i className="fa-solid fa-user-plus me-1"></i>
                        Seleccionar todos sin match para crear ({noMatchCount})
                      </button>
                      {createNewCount > 0 && (
                        <span className="badge bg-success">{createNewCount} marcados para crear</span>
                      )}
                    </div>
                  )}

                  {/* Match table */}
                  <div className="table-responsive mb-4" style={{ maxHeight: '500px' }}>
                    <table aria-label="Tabla de Padron Miembros Oficiales" className="table table-sm table-hover">
                      <thead className="table-dark sticky-top">
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">CSV: Nombre</th>
                          <th scope="col">CSV: Correo</th>
                          <th scope="col">CSV: Tipo</th>
                          <th scope="col">Estado</th>
                          <th scope="col">Match: Nombre</th>
                          <th scope="col">Match: Correo</th>
                          <th scope="col">Colección</th>
                          <th scope="col">Conf.</th>
                          <th scope="col">Método</th>
                          <th scope="col">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMatches.map((entry, i) => {
                          const globalIndex = matches.indexOf(entry);
                          return (
                            <tr
                              key={i}
                              className={entry.rejected ? 'table-light text-decoration-line-through' : entry.confirmed ? 'table-success' : ''}
                            >
                              <td>{globalIndex + 1}</td>
                              <td><strong>{entry.csv.nombre}</strong></td>
                              <td><small>{entry.csv.correo}</small></td>
                              <td>
                                <span className={`badge ${entry.csv.tipo === 'AFILIADO' ? 'bg-info' : 'bg-secondary'}`}>
                                  {entry.csv.tipo}
                                </span>
                              </td>
                              <td>
                                {entry.status === 'auto' && <span className="badge bg-success">Auto</span>}
                                {entry.status === 'review' && <span className="badge bg-warning text-dark">Revisar</span>}
                                {entry.status === 'no_match' && <span className="badge bg-danger">Sin match</span>}
                              </td>
                              <td>{entry.member?.nombre || '-'}</td>
                              <td><small>{entry.member?.correo || '-'}</small></td>
                              <td>
                                {entry.member && (
                                  <span className={`badge ${entry.member.collection === 'asesor' ? 'bg-info' : 'bg-secondary'}`}>
                                    {entry.member.collection === 'asesor' ? 'Afiliado' : 'Asociado'}
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className={`fw-bold ${entry.confidence >= 80 ? 'text-success' : entry.confidence >= 60 ? 'text-warning' : 'text-danger'}`}>
                                  {entry.confidence}%
                                </span>
                              </td>
                              <td><small>{entry.method}</small></td>
                              <td>
                                {/* Match sugerido Y no rechazado → mostrar Check/Tacha */}
                                {entry.member && !entry.rejected && (
                                  <div className="btn-group btn-group-sm">
                                    <button
                                      className={`btn ${entry.confirmed ? 'btn-success' : 'btn-outline-success'}`}
                                      onClick={() => toggleConfirm(globalIndex)}
                                      title="Confirmar: es la misma persona"
                                    >
                                      <i className="fa-solid fa-check"></i>
                                    </button>
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => toggleReject(globalIndex)}
                                      title="Rechazar: son personas distintas"
                                    >
                                      <i className="fa-solid fa-times"></i>
                                    </button>
                                  </div>
                                )}
                                {/* Sin match O rechazado → mostrar Crear nuevo */}
                                {(entry.status === 'no_match' || entry.rejected) && (
                                  <div className="d-flex gap-1 align-items-center">
                                    {entry.rejected && (
                                      <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => toggleReject(globalIndex)}
                                        title="Deshacer rechazo"
                                      >
                                        <i className="fa-solid fa-undo"></i>
                                      </button>
                                    )}
                                    <button
                                      className={`btn btn-sm ${entry.createNew ? 'btn-success' : 'btn-outline-success'}`}
                                      onClick={() => toggleCreateNew(globalIndex)}
                                      title="Crear perfil nuevo en Firestore"
                                    >
                                      <i className="fa-solid fa-user-plus me-1"></i>
                                      {entry.createNew ? 'Se creará' : 'Crear nuevo'}
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Members to remove */}
                  {toRemove.length > 0 && (
                    <div className="card border-warning mb-4">
                      <div className="card-header bg-warning bg-opacity-25">
                        <strong>Miembros a desmarcar ({toRemove.filter(m => !m.keep).length})</strong>
                        <small className="d-block text-muted">
                          Estos miembros son actualmente oficiales pero NO están en el CSV.
                          Se les quitará el estatus oficial a menos que los marques para mantener.
                        </small>
                      </div>
                      <div className="card-body p-0">
                        <div className="table-responsive" style={{ maxHeight: '250px' }}>
                          <table aria-label="Tabla de Padron Miembros Oficiales" className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th scope="col">Nombre</th>
                                <th scope="col">Correo</th>
                                <th scope="col">Tipo</th>
                                <th scope="col">Mantener</th>
                              </tr>
                            </thead>
                            <tbody>
                              {toRemove.map((m, i) => (
                                <tr key={i} className={m.keep ? 'table-success' : ''}>
                                  <td>{m.nombre}</td>
                                  <td><small>{m.correo}</small></td>
                                  <td>
                                    <span className={`badge ${m.collection === 'asesor' ? 'bg-info' : 'bg-secondary'}`}>
                                      {m.collection === 'asesor' ? 'Afiliado' : 'Asociado'}
                                    </span>
                                  </td>
                                  <td>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={m.keep}
                                      onChange={() => toggleKeep(i)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="d-flex gap-3">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => { setStep('upload'); setMatches([]); setToRemove([]); }}
                    >
                      <i className="fa-solid fa-arrow-left me-2"></i>Volver
                    </button>
                    <button
                      className="btn btn-outline-success"
                      onClick={exportarMatching}
                    >
                      <i className="fa-solid fa-file-csv me-2"></i>Exportar Matching
                    </button>
                    <button
                      className="btn btn-success btn-lg"
                      onClick={aplicarPadron}
                      disabled={confirmedCount === 0 && createNewCount === 0}
                    >
                      <i className="fa-solid fa-clipboard-check me-2"></i>
                      Aplicar Padrón ({confirmedCount} existentes{createNewCount > 0 ? ` + ${createNewCount} nuevos` : ''})
                    </button>
                  </div>
                </>
              )}

              {/* ============ STEP: APPLYING ============ */}
              {step === 'applying' && (
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4 text-center">
                    <div className="spinner-border text-primary mb-3" role="status"></div>
                    <h5>Aplicando padrón...</h5>
                    <p className="text-muted">No cierres esta página.</p>
                  </div>
                </div>
              )}

              {/* ============ STEP: DONE ============ */}
              {step === 'done' && stats && (
                <div className="card border-success shadow-sm">
                  <div className="card-body p-4">
                    <div className="text-center mb-4">
                      <i className="fa-solid fa-circle-check text-success" style={{ fontSize: '3rem' }}></i>
                      <h4 className="mt-3">Padrón Aplicado Exitosamente</h4>
                    </div>

                    <div className="row g-3">
                      <div className="col-md-3">
                        <div className="card bg-success bg-opacity-10 border-0">
                          <div className="card-body text-center">
                            <h3 className="text-success">{stats.marcados}</h3>
                            <small>Miembros marcados</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-warning bg-opacity-10 border-0">
                          <div className="card-body text-center">
                            <h3 className="text-warning">{stats.desmarcados}</h3>
                            <small>Desmarcados</small>
                          </div>
                        </div>
                      </div>
                      {stats.creados > 0 && (
                        <div className="col-md-2">
                          <div className="card bg-info bg-opacity-10 border-0">
                            <div className="card-body text-center">
                              <h3 className="text-info">{stats.creados}</h3>
                              <small>Nuevos creados</small>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="col-md-2">
                        <div className="card bg-primary bg-opacity-10 border-0">
                          <div className="card-body text-center">
                            <h3 className="text-primary">{stats.marcadasOficial}</h3>
                            <small>Props oficiales</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-secondary bg-opacity-10 border-0">
                          <div className="card-body text-center">
                            <h3 className="text-secondary">{stats.marcadasNoOficial}</h3>
                            <small>Props no oficiales</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 text-center">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => {
                          setStep('upload');
                          setCsvRows([]);
                          setMatches([]);
                          setToRemove([]);
                          setProgress([]);
                          setStats(null);
                        }}
                      >
                        <i className="fa-solid fa-redo me-2"></i>Ejecutar nuevo padrón
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ============ LOG DE PROGRESO ============ */}
              {progress.length > 0 && (
                <div className="card mt-4 border-0 bg-dark text-light">
                  <div className="card-header bg-dark border-bottom border-secondary">
                    <small className="text-muted">Log de progreso</small>
                  </div>
                  <div className="card-body p-3" style={{ maxHeight: '200px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {progress.map((msg, i) => (
                      <div key={i}>{msg}</div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="" />
      <Footer />
    </>
  );
}
